import {
  TOKEN_USAGE_PERCENT_MAX,
  TOKEN_USAGE_TIER_CRITICAL_MIN_PERCENT,
  TOKEN_USAGE_TIER_ELEVATED_MIN_PERCENT,
} from "../constants/token-usage";
import type {
  IBuildUsageThresholdMetaLineProps,
  IResolveUsageThresholdBodyProps,
  IResolveUsageThresholdPercentProps,
} from "../types/notifications";
import { EUsageThresholdSeverityTier } from "../types/notifications";
import { ETokenBudgetScope, ETokenBudgetUnit } from "../types/token-budget";
import { formatCompactUsageLimitAmount } from "./usage-limit-amount";

// NOTE: The scope decides the wording, and whether the server knew the org /
// project name decides between the named and the anonymous variant — i18next
// would otherwise interpolate a blank into a sentence built around that name.
export const resolveUsageThresholdBody = ({
  data,
  translate,
}: IResolveUsageThresholdBodyProps): string => {
  const values = {
    threshold: String(data.threshold),
    kind: translate(`type.usage_threshold_reached.kind_${data.usageKind}`, {}),
    organizationName: data.organizationName ?? "",
    projectTitle: data.projectTitle ?? "",
  };

  if (data.scope === ETokenBudgetScope.ORGANIZATION) {
    return data.organizationName
      ? translate(
          "type.usage_threshold_reached.body_organization_named",
          values,
        )
      : translate("type.usage_threshold_reached.body_organization", values);
  }

  if (data.scope === ETokenBudgetScope.USER) {
    return data.organizationName
      ? translate("type.usage_threshold_reached.body_user_named", values)
      : translate("type.usage_threshold_reached.body_user", values);
  }

  if (data.scope === ETokenBudgetScope.PROJECT) {
    return data.projectTitle
      ? translate("type.usage_threshold_reached.body_project_named", values)
      : translate("type.usage_threshold_reached.body_project", values);
  }

  return translate("type.usage_threshold_reached.body_personal", values);
};

// NOTE: Resolved by `>=` so any intermediate value degrades to the tier below.
export const resolveUsageThresholdSeverityTier = (
  threshold: number,
): EUsageThresholdSeverityTier => {
  if (threshold >= TOKEN_USAGE_PERCENT_MAX) {
    return EUsageThresholdSeverityTier.LIMIT_REACHED;
  }
  if (threshold >= TOKEN_USAGE_TIER_CRITICAL_MIN_PERCENT) {
    return EUsageThresholdSeverityTier.CRITICAL;
  }
  if (threshold >= TOKEN_USAGE_TIER_ELEVATED_MIN_PERCENT) {
    return EUsageThresholdSeverityTier.ELEVATED;
  }
  return EUsageThresholdSeverityTier.WARNING;
};

// NOTE: Width of the meter fill only, never a second number that would clash
// with the milestone the headline states (e.g. "reached 95%" beside a 96% chip).
// Clamped for the overshoot an in-flight action can cause, and a missing limit
// leaves the crossed threshold as the best available approximation.
export const resolveUsageThresholdPercent = ({
  usedTokens,
  limitTokens,
  threshold,
}: IResolveUsageThresholdPercentProps): number => {
  if (limitTokens > 0) {
    return Math.min(
      TOKEN_USAGE_PERCENT_MAX,
      Math.round((usedTokens / limitTokens) * TOKEN_USAGE_PERCENT_MAX),
    );
  }
  return threshold;
};

// NOTE: One quiet line — "8K / 10K tokens · Resets in 26 days" — carrying the
// raw numbers and the recovery time without competing with the headline. A
// reset date the client cannot parse drops that half rather than the whole line.
//
// NSAI-5612 — a cost cap says "$4.20 / $5.00" and drops the word "tokens",
// which would otherwise put a unit on a figure that is not one. The key is
// chosen by unit rather than the numbers being reformatted in place, because
// the two sentences differ by more than their numerals in ru and ar.
export const buildUsageThresholdMetaLine = ({
  usedTokens,
  limitTokens,
  unit = ETokenBudgetUnit.TOKENS,
  resetAt,
  translate,
  formatResetDistance,
}: IBuildUsageThresholdMetaLineProps): string => {
  const usageLabel = translate(
    unit === ETokenBudgetUnit.USD
      ? "type.usage_threshold_reached.used_of_limit_cost"
      : "type.usage_threshold_reached.used_of_limit",
    {
      used: formatCompactUsageLimitAmount({ amount: usedTokens, unit }),
      limit: formatCompactUsageLimitAmount({ amount: limitTokens, unit }),
    },
  );

  const resetDate = new Date(resetAt);
  if (Number.isNaN(resetDate.getTime())) {
    return usageLabel;
  }

  const resetLabel = translate("type.usage_threshold_reached.resets", {
    when: formatResetDistance(resetDate),
  });

  return `${usageLabel} · ${resetLabel}`;
};
